import initSentry from '@readme/backend/sentry/browser';
import { useCallback, useContext, useRef } from 'react';

import { ConfigContext, UserContext, ProjectContext } from '@core/context';
import useEnvInfo from '@core/hooks/useEnvInfo';

const useSentry = component => {
  const config = useContext(ConfigContext);
  const user = useContext(UserContext);
  const { project } = useContext(ProjectContext);

  const sentryRef = useRef(null);
  const env = useEnvInfo();

  // Configure Sentry as early as we can (i.e. before mounting) and only when
  // running on the client.
  if (env.isClient && !sentryRef.current) {
    const Sentry = initSentry(component, config);

    if (user) {
      Sentry.setUser({
        id: user?._id || user?.id || '',
        name: user?.name || '',
      });
    }

    if (project) {
      Sentry.setContext('project', {
        id: project._id || '',
        name: project.name || '',
        subdomain: project.subdomain || '',
      });
    }

    // Mark as initialized so we don't do this again.
    sentryRef.current = Sentry;
  }

  return useCallback((err, errData) => {
    // eslint-disable-next-line no-console
    console.error(err);

    sentryRef.current.withScope(scope => {
      if (errData && errData.supportErrorCode) {
        scope.setTag('support-code', errData.supportErrorCode);
      }

      sentryRef.current.captureException(err);
    });
  }, []);
};

export default useSentry;
